import { Box, Button, Checkbox, FormControlLabel, Modal, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import React, { useState, useEffect } from 'react';
import { DuplicateEventPayload } from '../../services/react-query/events/useDuplicateEvent';

type EventDuplicateModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (payload: DuplicateEventPayload) => void;
  defaultName: string;
  itemName: string;
};

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 420,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 3,
};

const EventDuplicateModal: React.FC<EventDuplicateModalProps> = ({ isOpen, onCancel, onConfirm, defaultName, itemName }) => {
  const [name, setName] = useState(defaultName);
  const [date, setDate] = useState<DateTime | null>(null);
  const [options, setOptions] = useState({
    employees: true,
    contacts: true,
    attachments: true,
  });

  // Keep last chosen values even when reopened → do NOT reset options
  useEffect(() => {
    if (isOpen) setName(defaultName);
  }, [isOpen, defaultName]);

  const handleToggle = (key: keyof typeof options) => {
    setOptions({ ...options, [key]: !options[key] });
  };

  const handleConfirm = () => {
    if (!name || !date) return;
    onConfirm({
      name: name,
      start_at: date.toISO()!, // only date needed
      copy_options: options,
    });
  };

  const confirmDisabled = !name || !date;

  return (
    <Modal open={isOpen} onClose={onCancel}>
      <Box sx={modalStyle}>
        <Typography variant="h6" mb={2}>
          Duplicate {itemName}
        </Typography>

        <TextField fullWidth label="New Name" value={name} onChange={(e) => setName(e.target.value)} sx={{ mb: 2 }} />

        <DatePicker label="New Start Date" value={date} onChange={(v) => setDate(v)} sx={{ width: '100%', mb: 2 }} />

        <Typography fontWeight="bold" mt={1}>
          Optional Sections to Duplicate
        </Typography>

        <Box display="flex" flexDirection="column" mt={1}>
          <FormControlLabel control={<Checkbox checked={options.employees} onChange={() => handleToggle('employees')} />} label="Employees" />
          <FormControlLabel control={<Checkbox checked={options.contacts} onChange={() => handleToggle('contacts')} />} label="Contacts" />
          <FormControlLabel control={<Checkbox checked={options.attachments} onChange={() => handleToggle('attachments')} />} label="Attachments" />
        </Box>
        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirm} disabled={confirmDisabled}>
            Duplicate
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EventDuplicateModal;
